import { useState } from "react";
import { ChatBubbleEmpty, EditPencil } from "iconoir-react";
import css from "./InlineFeedbackTrigger.module.css";

const STAR_PATH =
  "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";

function StarIcon({ filled, size = 16 }) {
  return (
    <svg
      className="shrink-0"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d={STAR_PATH}
        fill={filled ? "#FFD700" : "none"}
        stroke={filled ? "#FFD700" : "#9CA3AF"}
        strokeWidth={filled ? 0 : 1.5}
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function InlineFeedbackTrigger({
  rating = 0,
  onOpenFeedback,
  onStarClick,
  patientId,
  liveMessage,
  commentPreview,
}) {
  const [hoveredStar, setHoveredStar] = useState(0);
  const displayRating = Number(rating) || 0;
  const hasRating = displayRating > 0;
  const visualRating = hoveredStar || displayRating;
  const previewText = typeof commentPreview === "string" ? commentPreview.trim() : "";

  return (
    <div className="w-full min-w-0" data-tutorial="leave_feedback">
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveMessage}
      </div>

      <div
        id={`leave_feedback_${patientId}`}
        className={`${css.pill} relative flex w-full items-center justify-between gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-1.5`}
      >
        <button
          type="button"
          className="absolute inset-0 rounded-full"
          aria-label={hasRating ? "Edit your feedback" : "Share your experience"}
          title={previewText || undefined}
          onClick={onOpenFeedback}
        />
        <span className="relative pointer-events-none flex shrink-0 items-center gap-1.5 text-[13px] font-medium leading-snug text-[#4a4a5a]">
          {hasRating ? (
            <EditPencil width={16} height={16} aria-hidden="true" />
          ) : (
            <ChatBubbleEmpty width={16} height={16} aria-hidden="true" />
          )}
          {hasRating ? "Edit your feedback" : "Share your experience"}
        </span>
        {previewText ? (
          <span className={css.preview} aria-hidden="true">
            {previewText}
          </span>
        ) : null}

        <span
          className="relative z-10 flex shrink-0 items-center justify-end gap-0.5"
          role="radiogroup"
          aria-label="Feedback rating"
          onMouseLeave={() => setHoveredStar(0)}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={star === displayRating}
              aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
              className="cursor-pointer rounded p-0.5 transition-transform hover:scale-110"
              onMouseEnter={() => setHoveredStar(star)}
              onClick={() => onStarClick?.(star)}
            >
              <StarIcon filled={star <= visualRating} size={20} />
            </button>
          ))}
        </span>
      </div>
    </div>
  );
}
